import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  SecurityContext,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxMasonryComponent } from 'ngx-masonry';
import { AppComponent } from '../app.component';
import { UrlService } from '../model/url.service';
import { GlobalService } from '../model/global.service';
import { SugarService } from '../model/sugar.service';
import { ComponentsService } from '../model/components.service';
import { MetaData } from '../shared/metadata/metadata.service';
import { ModalFormComponent } from '../modal-form/modal-form.component';
import { SocialShareComponent } from '../social-share/social-share.component';
import { UniversalPlatformService } from '../shared/platform/universal-platform.service';
import { ApiData } from '../shared/utils/api-data';
import { CollectionsPerPageService } from '../shared/collections/collections-per-page.service';
import { ExhibitionsColletionSetter } from '../shared/collections/api-collection-setters';
import * as imagesLoaded from 'imagesloaded';
import { AutoUnsubscribe } from '../decorators/autounsubscribe.decorator';
import { getExpandText } from '../helpers/helpers';
import { getIndicesOf } from '../helpers/string-helpers';

@AutoUnsubscribe()
@Component({
  selector: 'app-exhibitions',
  templateUrl: './exhibitions.component.html',
})
export class ExhibitionsComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChildren('masonryItem') set masonryItem(items: QueryList<ElementRef>) {
    if (!this.masonryContainer) {
      return;
    }

    setTimeout(() => {
      let maxH = 0;

      items.forEach((item: ElementRef) => {
        if (item.nativeElement.offsetHeight > maxH) {
          maxH = item.nativeElement.offsetHeight;
        }
      });
      this.masonryContainer._msnry.$element[0].style.minHeight = maxH + 'px';
    }, 1000);
  }

  get hasPagination(): boolean {
    return this._currentExhibitions && (this._currentExhibitions || []).length > 0;
  }

  get getExpandText() {
    return getExpandText(this.isDescriptionExpanded);
  }

  get masonryContainer() {
    return this._masonryContainer;
  }

  @ViewChild('masonryContainer') set masonryContainer(item: NgxMasonryComponent) {
    this._masonryContainer = item;
  }

  @ViewChild('masonry')
  masonry: NgxMasonryComponent;
  isFourColumns: 'true' | 'false';
  @ViewChild('recommendationModal')
  recommendationModal: ModalFormComponent;
  @ViewChild('socialShare')
  socialShare: SocialShareComponent;
  public settings: { [key: string]: any };
  public _currentExhibitions: any;
  public _archive: any;
  public _imagesTypes: string[];
  public _recommendedObject: any;
  zoomImages: boolean;
  cutDescription: boolean;
  cutDescriptionMaxLength: number;
  isDescriptionExpanded = false;
  activeTextElementsOnHoverEnabled = false;
  private _csSub: any;
  private _dataSub: any;
  private _preloadingImages: boolean;

  private _masonryContainer;

  constructor(
    public urlService: UrlService,
    public domSanitizer: DomSanitizer,
    public globalService: GlobalService,
    private sugarService: SugarService,
    private componentsService: ComponentsService,
    private platformService: UniversalPlatformService,
    private rootComp: AppComponent,
    private metadata: MetaData,
    private perPageService: CollectionsPerPageService,
  ) {
    this.activeTextElementsOnHoverEnabled = this.globalService.settings.global.activeTextElementsOnHoverEnabled === 'true';
    this.cutDescription = this.globalService.settings.global.cutDescriptions === 'true';
    this.cutDescriptionMaxLength = Number(this.globalService.settings.global.cutDescriptionMaxLength);
    if (isNaN(this.cutDescriptionMaxLength)) {
      this.cutDescriptionMaxLength = 400;
    }
    this.zoomImages = this.globalService.settings.global.sliderRotation === 'true';
    this.settings = {};
    this._preloadingImages = false;

    this.globalService.loadGlobalSettings().subscribe(settings => {
      if (settings.__loaded) {
        this.isFourColumns = settings.global.isFourColumns;
      }
    });
  }

  description(currentExhibition: any) {
    const plain = (
      this.domSanitizer.sanitize(SecurityContext.HTML, currentExhibition.description_short[this.urlService.langCode])
      || ''
    ).trim();
    const spacesPositions = getIndicesOf(plain, ' ');

    this.cutDescriptionMaxLength = spacesPositions.filter(pos => pos >= this.cutDescriptionMaxLength)[0];

    this.cutDescription = this.cutDescription && (plain || []).length > this.cutDescriptionMaxLength;

    return this.cutDescription && !this.isDescriptionExpanded
      ? (plain.slice(0, this.cutDescriptionMaxLength)) + '...'
      : currentExhibition.description_short[this.urlService.langCode];
  }

  toggleDescription() {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  ngOnInit(): void {
    this.rootComp.fbService.updateCommonOg();

    this.sugarService.setCollection('current-exhibitions', {
      sugarNode: 'exhibitions',
      parameters: {
        expand: 'images',
        // "filter[active]": '1',
        'filter[status][0]': 'exhibition_status_in_preparation',
        'filter[status][1]': 'exhibition_status_ongoing',
        sort: '-date_from',
        'per-page': 10,
      },
    });
    this.sugarService.setCollection('exhibitions-archive', {
      sugarNode: 'exhibitions',
      parameters: {
        expand: 'images',
        // "filter[active]": '1',
        'filter[status]': 'exhibition_status_completed',
        sort: '-date_from',
        'per-page': 10,
      },
      availableSorting: [
        { id: '-date_from', title: 'exhibitions.sort-date-latest' },
        { id: 'date_from', title: 'exhibitions.sort-date-oldest' },
        { id: 'name', title: 'exhibitions.sort-title-a-z' },
        { id: '-name', title: 'exhibitions.sort-title-z-a' },
      ],
    });

    const collectionSetter = new ExhibitionsColletionSetter(
      this.urlService,
      this.sugarService,
      this.platformService,
      this.globalService,
      this.domSanitizer,
    );

    this._dataSub = this.sugarService.getData().subscribe(data => {
      if (data.collectionId === 'current-exhibitions') {
        const currentExhibitions = collectionSetter.setup(data.data, 'current-exhibitions');
        this._imagesTypes = [];

        for (let i = 0; i < (currentExhibitions || []).length; ++i) {
          this._imagesTypes.push((typeof currentExhibitions[i].image));

          if (0 === i) {
            this._recommendedObject = currentExhibitions[i];
          }
        }

        this._currentExhibitions = currentExhibitions;
      }

      if (data.collectionId === 'exhibitions-archive') {
        const archive = collectionSetter.setupArchive(data.data, 'exhibitions-archive');
        this._archive = [];

        const images: any[] = [];
        let image: any;

        this._preloadingImages = true;

        for (let a = 0; a < (archive || []).length; ++a) {
          if (this.platformService.isBrowser) {
            if (archive[a].images && (archive[a].images || []).length) {
              image = new Image();
              image.src = archive[a].images[0] ? archive[a].images[0].thumbnails.medium.url : 'assets/images/placeholder-450x340.jpg';
              images.push(image);
            }
          }

          this._archive.push(archive[a]);
        }

        if (this.platformService.isBrowser) {
          const imgLoaded = imagesLoaded(images);

          // wszystkie załadowane
          imgLoaded.on('done', () => {
            this._afterPreload();
          });

          // przynajmniej jeden zwrócił błąd
          imgLoaded.on('fail', () => {
            this._afterPreload();
          });
        } else {
          this._afterPreload();
        }
      }
    });

    this.sugarService.refreshData('exhibitions-archive');

    // metadata
    this._csSub = this.componentsService.getComponents().subscribe((apiData) => {
      const data = new ApiData(apiData);

      if (!data.typeIs('exhibitions')) {
        return;
      }

      if (data.hasMainContents()) {
        this.metadata.setCollectionPageMetaFromMainContents(data.main_contents);
      }

      if (data.hasMainblock()) {
        this.settings.items_per_page = parseInt(data.mainblock.items_per_page, 10);
        this.settings.items_per_page_change_enabled = parseInt(data.mainblock.items_per_page_change_enabled, 10) > 0;

        this.perPageService.setDefault('current-exhibitions', this.settings.items_per_page);
        this.perPageService.setSaving('current-exhibitions', this.settings.items_per_page_change_enabled);

        this.sugarService.updateParam(
          'current-exhibitions',
          'per-page',
          '' + (
            this.perPageService.getValue('current-exhibitions')
              ? this.perPageService.getValue('current-exhibitions')
              : this.settings.items_per_page
          ) + '',
        );

        this.perPageService.setDefault('exhibitions-archive', this.settings.items_per_page);
        this.perPageService.setSaving('exhibitions-archive', this.settings.items_per_page_change_enabled);

        this.sugarService.updateParam(
          'exhibitions-archive',
          'per-page',
          '' + (
            this.perPageService.getValue('current-exhibitions')
              ? this.perPageService.getValue('current-exhibitions')
              : this.settings.items_per_page
          ) + '',
        );
      }

      this.sugarService.refreshData('current-exhibitions');
      this.sugarService.refreshData('exhibitions-archive');
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.rootComp.bodyClass = 'exhibitions';
    });
  }

  ngOnDestroy(): void {
    this._csSub.unsubscribe();
    this._dataSub.unsubscribe();
  }

  openRecommendationModal(index: number, evt?: any): void {
    this._recommendedObject = this._currentExhibitions[index];

    if (evt && typeof evt.prefentDefault !== 'undefined') {
      evt.preventDefault();
    }

    setTimeout(() => {
      this.recommendationModal.openModal();
    }, 100);
  }

  showPreloader(): boolean {
    if (!this._currentExhibitions) {
      return true;
    }

    return this._preloadingImages;
  }

  private _afterPreload(): void {
    this._preloadingImages = false;

    if (this.platformService.isBrowser && this.masonry) {
      setTimeout(() => this.masonry.layout(), 100);
    }
  }

}
